import { Inject, Injectable, LOCALE_ID, OnDestroy } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { createSelector, select, Store } from '@ngrx/store';
import { get, toLower } from 'lodash-es';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AppState } from 'src/app/app.state';
import { selectAppConfig } from 'src/app/core/application-config/application-config.selectors';
import { APPCONFIG_SUPPORT_NUMBER } from 'src/app/shared/enums/application-config.enums';
import { validString } from 'src/app/shared/utilities/types.utils';
import { selectMerchantId } from '../client-configuration/client-configuration.selectors';
import { LoggerService } from '../logger/logger.service';
import { selectLocales } from './locale.selectors';
import { Locales } from './locale.state';

/**
 * Handles looking up all locales and returning the strings.
 */
@Injectable({
	providedIn: 'root',
})
export class LocaleService implements OnDestroy {
	protected _merchantId: string;
	protected _locales: Locales;

	private _supportNumberValue: string;

	private _destroy$ = new Subject<void>();

	/**
	 * @param _localeId The configured locale id.
	 * @param _store$ The store state.
	 * @param _logger Our console logger.
	 * @param _sanitizer The DOM _sanitizer for using SafeHTML
	 */
	constructor(
		@Inject(LOCALE_ID) private _localeId: string,
		protected _store$: Store<AppState>,
		protected _logger: LoggerService,
		private _sanitizer: DomSanitizer
	) {
		this._store$
			.pipe(
				select(
					createSelector(
						selectLocales,
						selectMerchantId,
						selectAppConfig([APPCONFIG_SUPPORT_NUMBER]),
						(applicationLocales, merchantId, appConfigs) => {
							return {
								applicationLocales,
								merchantId,
								appConfigs,
							};
						}
					)
				),
				takeUntil(this._destroy$)
			)
			.subscribe(({ applicationLocales, merchantId, appConfigs }) => {
				const supportNumber = appConfigs?.supportNumber;
				this._locales = applicationLocales.locales;
				this._merchantId = merchantId;

				if (validString(supportNumber)) {
					this._supportNumberValue = supportNumber;
				}
			});
	}

	ngOnDestroy(): void {
		this._destroy$.next();
		this._destroy$.complete();
	}

	/**
	 * Returns the array of searchable locales to use when receiving a section and target
	 * @param section The section
	 * @param target The target
	 * @param suffix The prefix
	 */
	getSearchableSectionTargets(section: string, target: string, suffix?: string): string[] {
		const sectionTarget = toLower(`${section}.${target}`);
		const searchMap: string[] = [`${sectionTarget}_${this._merchantId}`, `${sectionTarget}`];

		if (validString(suffix)) {
			searchMap.unshift(
				toLower(`${sectionTarget}_${suffix}_${this._merchantId}`),
				toLower(`${sectionTarget}_${suffix}`)
			);
		}

		return searchMap;
	}

	/**
	 * Returns the loaded locale sections.
	 */
	getLocaleSections(): string[] {
		return Object.keys(this._locales);
	}

	/**
	 * Returns a locale
	 * @param value The string of section and target separated by a period. E.g. `section.target`.
	 * @param suffix The suffix, if you want to make it more specific.
	 * @param asHtml Pass in true if you plan on using this as HTML with `[textContent]`
	 */
	get(value: any, suffix?: string): string;
	get(value: any, suffix?: string, asHtml?: boolean): SafeHtml;
	get(value: any, suffix?: string, asHtml?: boolean): SafeHtml {
		const newValue = validString(value) ? toLower(value) : '';
		const debugEnabled = this._logger.isEnabled();
		const [section, target] = newValue.split('.');
		const defaultValue = debugEnabled ? `[Missing Locale: ${section}.${target}]` : '';

		if (validString(section) && validString(target)) {
			const searchMap = this.getSearchableSectionTargets(section, target, suffix);
			const foundIndex: number = searchMap.findIndex((search) => {
				return validString(get(this._locales, search));
			});

			if (foundIndex > -1) {
				const foundValue = get(this._locales, searchMap[foundIndex]);
				const updatedValue: string = this._handleReplacements(foundValue.toString());

				return asHtml ? this._sanitizer.bypassSecurityTrustHtml(updatedValue) : updatedValue;
			}
			return defaultValue;
		} else if (!validString(newValue)) {
			return '';
		} else {
			return defaultValue;
		}
	}

	/**
	 * Find out if a locale exists.
	 * @param value The section.target string
	 * @param suffix The suffix if needed
	 * @returns If a locale exists for it
	 */
	has(value: any): boolean;
	has(value: any, suffix?: string): boolean {
		const newValue = validString(value) ? value : '';
		const [section, target] = newValue.split('.');

		if (validString(section) && validString(target)) {
			const searchMap = this.getSearchableSectionTargets(section, target, suffix);
			const foundIndex: number = searchMap.findIndex((search) => {
				return validString(get(this._locales, search));
			});

			return foundIndex > -1;
		}
		return false;
	}

	/**
	 * Find out if this is a last name first language.
	 */
	isLastNameFirst(): boolean {
		const parts = this._localeId.toString().split('-');
		return ['cn', 'jp', 'tw', 'vn', 'kp', 'kr', 'sg'].includes(toLower(parts[parts?.length - 1]));
	}

	private _handleReplacements(localeText: string): string {
		let updatedLocale: string = localeText;

		if (this._supportNumberValue) {
			updatedLocale = localeText.replace('@@supportNumber', this._supportNumberValue);
		}

		return updatedLocale;
	}
}
